import {
  Component,
  OnInit,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ClientSubscriptionService,
  TimeconversionService,
  ToastMessageService,
} from '@fitness-force/api';
import type {
  RecurringTemplateNonRecurringService,
  SubscriptionById,
} from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { StyleClassModule } from 'primeng/styleclass';

import { Observable } from 'rxjs';

@Component({
  selector: 'membership-sessions-edit',
  templateUrl: './membership-sessions-edit.component.html',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    StyleClassModule,
    ButtonModule,
    PopoverModule,
    InputNumberModule,
    InputTextModule,
    TranslateModule,
  ],
})
export class MembershipSessionsEditComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private cd = inject(ChangeDetectorRef);
  private toastMessageService = inject(ToastMessageService);
  translate = inject(TranslateService);
  private clientSubscriptionService = inject(ClientSubscriptionService);
  private timeService = inject(TimeconversionService);
  private toastService = inject(ToastMessageService);

  readonly visible = input(false);
  readonly subscription = input<SubscriptionById>();
  readonly clientId = input<number>(0);
  readonly tenantId = input<number>(0);
  readonly membershipId = input<number>(0);
  readonly closeEvent = output<any>({ alias: 'closeEvent' });
  readonly reloadSubscription = output<any>({ alias: 'reloadSubscription' });

  EditMembershipSessionsForm: UntypedFormGroup = new UntypedFormGroup({});
  isFormSubmitting: boolean = false;
  membership: RecurringTemplateNonRecurringService;
  memberships: RecurringTemplateNonRecurringService[] = [];

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    const subscriptionData = this.subscription();
    if (!subscriptionData) {
      return;
    }
    this.memberships =
      subscriptionData.recurring_template_non_recurring_services;
    this.membership = this.memberships.find(
      (o) => o.client_membership_id == this.membershipId(),
    )!;
    this.EditMembershipSessionsForm = this.fb.group({
      available_sessions: [this.membership.available_sessions || 0],
      total_sessions: [this.membership.service_sessions, Validators.required],
      comment: ['', Validators.required],
    });
  }

  UpdateMembershipSessions() {
    if (this.EditMembershipSessionsForm.valid) {
      this.changeMembershipSessions();
    } else {
      Object.keys(this.EditMembershipSessionsForm.controls).forEach((field) => {
        const control = this.EditMembershipSessionsForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.cd.detectChanges();
    }
  }

  changeMembershipSessions() {
    this.isFormSubmitting = true;
    let service = new Observable<any>();
    let patchObj: Array<{ op: string; path: string; value: any }> = [];
    if (
      this.CarriedOverSessionFormControl.value !=
      this.membership.available_sessions
    )
      patchObj.push({
        op: 'replace',
        path: '/available_session',
        value: this.CarriedOverSessionFormControl.value,
      });
    if (this.TotalSessionFormControl.value != this.membership.service_sessions)
      patchObj.push({
        op: 'replace',
        path: '/total_session',
        value: this.TotalSessionFormControl.value,
      });
    patchObj.push({
      op: 'replace',
      path: '/comment',
      value: this.CommentFormControl.value,
    });
    const subscriptionData = this.subscription();
    if (!subscriptionData) {
      return;
    }
    service = this.clientSubscriptionService.ChangeMembershipSessions(
      subscriptionData.tenant_id,
      this.clientId(),
      subscriptionData.id,
      this.membershipId(),
      patchObj,
    );
    service.subscribe({
      next: async (res: MembershipSessionsEditResponse) => {
        this.isFormSubmitting = false;
        this.translate
          .get('SUBSCRIPTIONS.MEMBERSHIP_SESSIONS.MSG_CHANGE_SESSIONS')
          .subscribe((translation) => {
            this.toastMessageService.showSuccess(translation);
          });
        await this.timeout(1000);
        const subscriptionData = this.subscription();
        if (subscriptionData) {
          subscriptionData.recurring_template_non_recurring_services.forEach(
            (o) => {
              if (o.client_membership_id == this.membershipId()) {
                o.available_sessions = res.availableSession;
                o.service_sessions = res.totalSession;
              }
            },
          );
        }
        this.closePopup(true);
      },
      error: (error: any) => {
        this.isFormSubmitting = false;
        this.toastService.showError(error);
        this.cd.detectChanges();
      },
    });
  }

  closePopup(value: boolean) {
    this.closeEvent.emit(this.subscription());
    this.closeEvent.emit(this.subscription());
  }

  reloadData(value: boolean) {
    this.reloadSubscription.emit(value);
  }

  get CarriedOverSessionFormControl(): UntypedFormControl {
    return this.EditMembershipSessionsForm.controls
      .available_sessions as UntypedFormControl;
  }

  get TotalSessionFormControl(): UntypedFormControl {
    return this.EditMembershipSessionsForm.controls
      .total_sessions as UntypedFormControl;
  }

  get CommentFormControl(): UntypedFormControl {
    return this.EditMembershipSessionsForm.controls
      .comment as UntypedFormControl;
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export interface MembershipSessionsEditResponse {
  startDate: string;
  endDate: string;
  sessionOwnerId: number;
  totalSession: number;
  availableSession: number;
  comment: string;
}
